import { useCallback, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { ConsoleLayout } from "@/components/console-layout";
import { LandingPage } from "@/pages/landing-page";
import { LoginPage } from "@/pages/login-page";
import { SignupPage } from "@/pages/signup-page";
import { DownloadPage } from "@/pages/download-page";
import { DashboardPage } from "@/pages/dashboard-page";
import { OrganizationsPage } from "@/pages/organizations-page";
import { ProjectsPage } from "@/pages/projects-page";
import { UsagePage } from "@/pages/usage-page";
import { AccountPage } from "@/pages/account-page";
import { AdminReleasesPage } from "@/pages/admin-releases-page";
import { AdminPricingPage } from "@/pages/admin-pricing-page";
import { AdminUsersPage } from "@/pages/admin-users-page";
import { BillingPage } from "@/pages/billing-page";
import { PricingPage } from "@/pages/pricing-page";
import {
  ContactPage,
  NotFoundPage,
  PrivacyPage,
  RefundPage,
  TermsPage,
} from "@/pages/legal-pages";
import {
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "@/pages/auth-flow-pages";
import { InvitationAcceptPage } from "@/pages/invitation-accept-page";
import { useAuthStore } from "@/stores/auth-store";
import { useIdleTimeout } from "@/lib/use-idle-timeout";
import { usePageMeta } from "@/lib/page-meta";

function ProtectedRoute({ children }: { children: JSX.Element }): JSX.Element {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();
  if (status === "loading" || status === "idle") {
    return (
      <main className="container py-12 text-sm text-muted-foreground">
        불러오는 중…
      </main>
    );
  }
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

/**
 * SuperAdminGuard 의 web 측 — Account.isSuperAdmin === true 만 통과.
 * 권한 부족이면 대시보드로 redirect (404 보다 친절).
 * ProtectedRoute 내부에서 사용한다는 가정 — auth 상태는 이미 인증됨.
 */
function SuperAdminRoute({ children }: { children: JSX.Element }): JSX.Element {
  const account = useAuthStore((s) => s.account);
  if (account?.isSuperAdmin !== true) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

export default function App(): JSX.Element {
  const hydrate = useAuthStore((s) => s.hydrate);
  const status = useAuthStore((s) => s.status);
  const signout = useAuthStore((s) => s.signout);
  const navigate = useNavigate();

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // 웹 전용 idle timeout — 60분 활동 없으면 자동 로그아웃 + /login 으로 이동.
  // 데스크톱 앱은 OS keychain + 자체 refresh 흐름이라 영구 세션, 웹은 공용
  // 환경 위험이 있어 분리 적용.
  const onIdleSignout = useCallback(() => {
    signout();
    navigate("/login", {
      replace: true,
      state: { reason: "idle-timeout" },
    });
  }, [navigate, signout]);
  useIdleTimeout(status === "authenticated", onIdleSignout);
  usePageMeta();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* 인증된 사용자 콘솔 — 사이드바 레이아웃 */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <ConsoleLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route
              path="admin/releases"
              element={
                <SuperAdminRoute>
                  <AdminReleasesPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/pricing"
              element={
                <SuperAdminRoute>
                  <AdminPricingPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <SuperAdminRoute>
                  <AdminUsersPage />
                </SuperAdminRoute>
              }
            />
          </Route>

          {/* 다운로드 안내는 사이드바 없이 별도 + 로그인 없이 공개 — 가입 전에 지원 OS·준비물을 확인할 수 있게 */}
          <Route path="/download" element={<DownloadPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
      <SiteFooter />
    </div>
  );
}
